import { Icon } from '../Icon'
import { WalkthroughCard } from './WalkthroughCard'
import type {
  LivePulsePayload,
  LiveTimelineItem,
  LiveFeedEntry,
  LiveShift
} from '../../types'

// LiveManagerOverview — four .day-card sections inside the dark
// LiveDayView body (Live View PR L3 / spec 33 §5). Layout per
// prototype L2652-2733: Timeline on the left (1.3fr column),
// Walkthrough + Live Activity + Who's On/Off stacked on the right
// (1fr column).
//
// Coverage / KPI strip from the prototype is deliberately deferred —
// the work plan locks L3 at four cards (Chris's note in the L3
// kickoff confirms). A later batch can add the strip without a
// schema change.

interface Props {
  eventId: number
  pulse: LivePulsePayload
  canEdit: boolean
  onViewPerson?: (userId: number) => void
  showToast: (msg: string) => void
}

export function LiveManagerOverview({
  eventId,
  pulse,
  canEdit,
  onViewPerson,
  showToast
}: Props) {
  const onShift = pulse.shifts.filter((s) => s.signedOffAt == null)
  const offShift = pulse.shifts.filter((s) => s.signedOffAt != null)

  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 18 }}
    >
      {/* LEFT column — Timeline */}
      <div>
        <TimelineCard items={pulse.timeline} />
      </div>

      {/* RIGHT column — Walkthrough on top, Live Activity, Who's On/Off */}
      <div className="space-y-4">
        <WalkthroughCard
          eventId={eventId}
          walkthrough={pulse.walkthrough}
          canEdit={canEdit}
          showToast={showToast}
        />
        <LiveActivityCard entries={pulse.feed} />
        <WhosOnOffCard
          onShift={onShift}
          offShift={offShift}
          onViewPerson={onViewPerson}
        />
      </div>
    </div>
  )
}

// ---- Timeline card ---------------------------------------------------------
//
// Matches prototype L2653-2682: rows are `gridTemplateColumns: 64px 1fr auto`,
// time monospace, title line-through for done, NOW chip (#ff8a65), NEXT chip
// (parchment), check-circle for done.

function TimelineCard({ items }: { items: LiveTimelineItem[] }) {
  return (
    <div className="day-card">
      <div className="day-card-head">Today's Schedule</div>
      {items.length === 0 && (
        <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.55)' }}>
          No services scheduled for today.
        </div>
      )}
      {items.map((item, i) => {
        const colour =
          item.status === 'done'
            ? 'rgba(245, 241, 234, 0.4)'
            : item.status === 'now'
              ? '#ff8a65'
              : item.status === 'next'
                ? '#fdfbf6'
                : 'rgba(245, 241, 234, 0.7)'
        const who = [item.origin, item.destination].filter(Boolean).join(' → ') ||
                    (item.loco ?? '')
        return (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '64px 1fr auto',
              gap: 12,
              padding: '10px 0',
              borderBottom: i === items.length - 1 ? 'none' : '1px solid rgba(245, 241, 234, 0.1)',
              alignItems: 'center'
            }}
          >
            <div className="font-mono font-semibold text-sm" style={{ color: colour }}>
              {item.time}
            </div>
            <div>
              <div
                className="font-semibold text-sm"
                style={{
                  color: colour,
                  textDecoration: item.status === 'done' ? 'line-through' : 'none'
                }}
              >
                {item.label}
              </div>
              {who && (
                <div className="text-xs" style={{ color: 'rgba(245, 241, 234, 0.5)' }}>
                  {who}
                </div>
              )}
            </div>
            {item.status === 'now' && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold"
                style={{ background: '#ff8a65', color: '#1a1a1a', letterSpacing: '0.06em' }}
              >
                NOW
              </span>
            )}
            {item.status === 'next' && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold"
                style={{
                  background: 'rgba(245, 241, 234, 0.15)',
                  color: '#fdfbf6',
                  letterSpacing: '0.06em'
                }}
              >
                NEXT
              </span>
            )}
            {item.status === 'done' && (
              <span style={{ color: 'rgba(245, 241, 234, 0.4)' }}>
                <Icon name="check-circle" size={14} />
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---- Live Activity card ----------------------------------------------------
//
// Matches prototype L2687-2700: feed list with time-mono + message +
// who-line, scrollable to 220px, "Log an update..." input at the
// bottom. Input is decorative in L3 — wires to a new endpoint in a
// later batch (the work plan calls this out as deferred).

function LiveActivityCard({ entries }: { entries: LiveFeedEntry[] }) {
  return (
    <div className="day-card">
      <div className="day-card-head">Live Activity</div>
      <div className="scrollbar-clean" style={{ maxHeight: 220, overflowY: 'auto' }}>
        {entries.length === 0 && (
          <div className="text-sm py-2" style={{ color: 'rgba(245, 241, 234, 0.55)' }}>
            Nothing recorded in the last few hours.
          </div>
        )}
        {entries.map((entry, i) => {
          const time = formatClockTime(entry.occurredAt)
          return (
            <div
              key={entry.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '46px 1fr',
                gap: 10,
                padding: '8px 0',
                borderBottom: i === entries.length - 1 ? 'none' : '1px solid rgba(245, 241, 234, 0.08)'
              }}
            >
              <div className="font-mono text-xs" style={{ color: 'rgba(245, 241, 234, 0.5)' }}>
                {time}
              </div>
              <div>
                <div className="text-sm">{entry.summary}</div>
                {entry.actorName && (
                  <div className="text-xs" style={{ color: 'rgba(245, 241, 234, 0.5)', marginTop: 1 }}>
                    {entry.actorName}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <input
        className="w-full mt-3"
        placeholder="Log an update…"
        style={{
          background: 'rgba(245, 241, 234, 0.05)',
          border: '1px solid rgba(245, 241, 234, 0.2)',
          color: '#fdfbf6',
          padding: '10px 12px',
          borderRadius: 6,
          fontSize: 13
        }}
        disabled
        title="Inline activity logging arrives in a later batch."
      />
    </div>
  )
}

// ---- Who's On / Off card ---------------------------------------------------
//
// Matches prototype L2701-2731. Two sections: "On Shift" (green dot,
// signed-on time) + "Off Shift" (grey dot, signed-off time). Clicking a
// person opens the PersonDutyModal in PR L4 — wired here via the
// optional onViewPerson callback.

function WhosOnOffCard({
  onShift,
  offShift,
  onViewPerson
}: {
  onShift: LiveShift[]
  offShift: LiveShift[]
  onViewPerson?: (userId: number) => void
}) {
  return (
    <div className="day-card">
      <div className="day-card-head flex items-center justify-between" style={{ display: 'flex' }}>
        <span>Who's On / Off</span>
        <span className="text-xs" style={{ color: 'rgba(245, 241, 234, 0.5)', fontFamily: 'inherit', fontWeight: 400 }}>
          {onShift.length} on · {offShift.length} off
        </span>
      </div>

      <div
        className="text-xs uppercase font-bold mb-2"
        style={{ color: '#80e8a8', letterSpacing: '0.08em' }}
      >
        On Shift
        {onViewPerson && (
          <span style={{ color: 'rgba(245, 241, 234, 0.4)', textTransform: 'none', fontWeight: 400, marginLeft: 6 }}>
            · tap to view their duties
          </span>
        )}
      </div>

      {onShift.length === 0 && (
        <div className="text-xs mb-3" style={{ color: 'rgba(245, 241, 234, 0.5)' }}>
          Nobody signed on yet.
        </div>
      )}
      {onShift.map((s) => (
        <ShiftRow
          key={s.id}
          shift={s}
          dotColour="#66bb6a"
          timeText={formatClockTime(s.signedOnAt)}
          onClick={onViewPerson ? () => onViewPerson(s.userId) : undefined}
        />
      ))}

      {offShift.length > 0 && (
        <div
          className="text-xs uppercase font-bold mt-3 mb-2"
          style={{ color: 'rgba(245, 241, 234, 0.5)', letterSpacing: '0.08em' }}
        >
          Signed Off
        </div>
      )}
      {offShift.map((s) => (
        <ShiftRow
          key={s.id}
          shift={s}
          dotColour="rgba(245, 241, 234, 0.4)"
          timeText={s.signedOffAt ? formatClockTime(s.signedOffAt) : '—'}
          dim
          onClick={onViewPerson ? () => onViewPerson(s.userId) : undefined}
        />
      ))}
    </div>
  )
}

function ShiftRow({
  shift,
  dotColour,
  timeText,
  dim,
  onClick
}: {
  shift: LiveShift
  dotColour: string
  timeText: string
  dim?: boolean
  onClick?: () => void
}) {
  const initials = shift.userAvatar ?? '?'
  const name = shift.userName ?? 'Unknown'
  const interactive = onClick != null
  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(e) => {
        if (!interactive) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.() }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 6,
        opacity: dim ? 0.7 : 1,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background 0.15s'
      }}
      onMouseEnter={(e) => {
        if (interactive) e.currentTarget.style.background = 'rgba(245, 241, 234, 0.08)'
      }}
      onMouseLeave={(e) => {
        if (interactive) e.currentTarget.style.background = 'transparent'
      }}
    >
      <div
        style={{
          width: 22, height: 22, borderRadius: '50%',
          background: 'rgba(155, 127, 42, 0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 700, color: '#fdfbf6',
          flexShrink: 0
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-xs font-semibold" style={{ color: '#fdfbf6' }}>{name}</div>
      </div>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: dotColour, flexShrink: 0 }} />
      <span className="font-mono text-xs" style={{ color: 'rgba(245, 241, 234, 0.6)' }}>{timeText}</span>
    </div>
  )
}

function formatClockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
